/**
 * Mapping des routes vers les permissions requises.
 * - string: Une seule permission requise
 * - string[]: Au moins une des permissions requise (OR)
 * - null: Route réservée aux super-admins uniquement
 * - undefined (route non listée): Pas de permission spécifique requise
 */
export const routePermissions: Record<string, string | string[] | null> = {
    // Dashboard
    '/dashboard': 'campaigns.view',

    // Campaigns
    '/campaigns': 'campaigns.view',
    '/campaigns/:id/edit': 'campaigns.edit',
    '/campaigns/:id/edit/flows': 'campaigns.edit',
    '/campaigns/categories/create': 'campaigns.edit',
    '/campaigns/categories/:id/edit': 'campaigns.edit',
    '/campaigns/categories/:id/form-types': 'campaigns.edit',
    '/campaigns/categories/:id/flows': 'campaigns.edit',

    // Flows
    '/flows': 'flows.view',
    '/flows/create': 'flows.create',
    '/flows/update': 'flows.edit',
    '/flows/statistics': 'flows.view',

    // Surveys
    '/surveys': 'surveys.view',
    '/surveys/create': 'surveys.create',
    '/surveys/update': 'surveys.view',  // Mode lecture seule si pas surveys.edit
    '/surveys/analytics': 'surveys.view',

    // Email Templates
    '/email-templates': 'email_templates.view',
    '/email-templates/create': 'email_templates.create',
    '/email-templates/update': 'email_templates.edit',

    // Reports / Documents
    '/reports': 'reports.view',
    '/reports/create': 'reports.create',
    '/documents': 'reports.view',

    // Settings - Granulaires
    '/settings': 'settings.view',
    '/settings/facilities': 'facility.view',
    '/settings/users': 'users.view',
    '/settings/roles': 'roles.view',
    '/settings/tags': 'tags.view',
    '/settings/facility-groups': 'facility.view',
    '/settings/mapping-keys': 'settings.view',
    '/settings/brandings': 'settings.view',
    // Facilities (Cardex) - Super-admin only
    '/facilities': null,

    // Super Admin - Réservé aux super-admins
    '/super-admin': null,
    '/super-admin/facilities': null,
    '/super-admin/analytics': null,
    '/super-admin/retention': null,
    '/super-admin/imports': null,
    '/super-admin/campaign-requests': null,
};

/**
 * Ordre de priorité des routes pour la redirection par défaut.
 * La première route accessible sera utilisée.
 */
export const defaultRouteOrder = [
    '/dashboard',
    '/campaigns',
    '/surveys',
    '/email-templates',
    '/reports',
    '/settings',
];

/**
 * Une route déclarée avec des paramètres (`/campaigns/:id/edit`) correspond-elle
 * à ce pathname ? Les segments `:param` acceptent n'importe quelle valeur.
 */
function matchesPattern(pattern: string, pathname: string): boolean {
    const patternSegments = pattern.split('/');
    const pathSegments = pathname.split('/');

    if (patternSegments.length !== pathSegments.length) {
        return false;
    }

    return patternSegments.every((segment, index) => segment.startsWith(':') || segment === pathSegments[index]);
}

/**
 * Trouve la permission requise pour un pathname donné.
 * Cherche une correspondance exacte, puis par motif paramétré, puis par préfixe.
 */
export function getRequiredPermission(pathname: string): string | string[] | null | undefined {
    // Correspondance exacte
    if (routePermissions[pathname] !== undefined) {
        return routePermissions[pathname];
    }

    // Motifs paramétrés, du plus spécifique au moins spécifique
    const matchingPattern = Object.keys(routePermissions)
        .filter(route => route.includes(':') && matchesPattern(route, pathname))
        .sort((a, b) => b.length - a.length)[0];

    if (matchingPattern) {
        return routePermissions[matchingPattern];
    }

    // Chercher par préfixe (du plus spécifique au moins spécifique)
    const sortedRoutes = Object.keys(routePermissions)
        .filter(route => pathname.startsWith(route))
        .sort((a, b) => b.length - a.length);

    if (sortedRoutes.length > 0) {
        return routePermissions[sortedRoutes[0]];
    }

    // Pas de permission requise pour cette route
    return undefined;
}
